<script lang="ts" setup>
import { CartProduct } from '~/lib/services/store/catalog/cart.dtos'
import { ProductEcommerceLite, ProductType } from '~/lib/services/store/catalog/catalog.dtos'
import { ProductLiteWithCategory } from '~/types'

const { trackAddToCart, trackSelectItem, trackViewItemList } = useGTM()
const { getCurrencyAcronym } = useFormatAmount()

const props = defineProps<{
  suggestiveProducts?: ProductLiteWithCategory[]
}>()

const emit = defineEmits(['product-action'])

const appStore = useAppStore()
const authStore = useAuthStore()
const restaurantStore = useRestaurantStore()
const { getProductRoute } = useMcdRoutes()
const { catalogServiceRef } = useStoreService()
const catalogService = catalogServiceRef()
const { t } = useLocale()

const mustShowSuggestive = computed(() => {
  if (!authStore.isUserLogged || !props.suggestiveProducts) return false
  const productsLength = appStore.cart.products.length
  // no products
  if (!productsLength) return false
  // check condition any product mcdia is present
  const areAnyMcDiaProducts = appStore.cart.products.some(cartProd => cartProd.isMcDia)
  if (areAnyMcDiaProducts) return false
  const maxLengthProducts = appStore.ecommerceConfig?.crossselling?.maxLines
  if (!maxLengthProducts || productsLength > maxLengthProducts) return false
  return !!props.suggestiveProducts.length
})

onMounted(() => {
  if (!props.suggestiveProducts?.length) return
  const gtmEcommerceItems = props.suggestiveProducts.map((item, index) =>
    gtmEcommerceItemFromLite({
      product: item.product,
      categoryTitle: item.category,
      itemListName: itemListNameFromProductType(item.product?.productType as ProductType),
      index: index + 1,
      currency: getCurrencyAcronym(),
    })
  )
  trackViewItemList(gtmEcommerceItems)
})

const addSuggestiveProduct = async (product: ProductEcommerceLite, index: number) => {
  const productTypeSelected = product.productType as ProductType
  const category = appStore.categories
    ?.filter(category => category.active)
    .find(cat => cat.products.some(({ identifier }) => identifier === product.identifier))
  if (!category) return

  // decide to add directly or navigate to detail
  const isCustomizable = !!product.customization
  if (!isCustomizable) {
    try {
      // fetch product detail
      const fetchProductInput = {
        countryCode: appStore.countryCode,
        restaurantId: restaurantStore.restaurant!.id,
        categoryId: category.idRfm,
        productId: product.identifier,
        area: restaurantStore.areaSelected!,
      }
      const productDetail = await catalogService.getProductDetail(fetchProductInput)

      // Assign the product type that comes from crossselling
      productDetail.productType = productTypeSelected

      // add directly to cart
      const productDTO = productToDTO(productDetail, 1)
      const price = productDetail.price.amount || productDetail.unifiedPrice?.amount || 0
      const computedPrice = {
        price,
        priceUnit: price,
      }
      const cartProduct: CartProduct = {
        ...productDTO,
        name: productDetail.name,
        genericName: productDetail.genericName,
        image: productDetail.imageUrl,
        categoryIdRfm: category.idRfm,
        categoryTitle: category.title,
        computedPrice,
        customization: '',
        isMcDia: !!productDetail.mcDia,
      }
      trackAddToCart([cartProduct], undefined)
      await appStore.addCartProduct(cartProduct, productDetail)
      emit('product-action')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error while adding suggestive product to cart', error)
    }
  } else {
    // track Select Item (passing productTypeSelected)
    trackSelectItem(product, category.title, index, productTypeSelected)
    // go to product detail to add crossSelling product
    // set state productType
    appStore.setProductTypeOnDetail(productTypeSelected)
    const restaurantInfo = useRestaurantStore().selectedRestaurant!.restaurant!
    const path = getProductRoute({
      restaurantInfo,
      categoryTitle: category.title,
      productName: product.name,
    })
    await navigateTo(path)
  }
}
</script>

<template>
  <div v-if="mustShowSuggestive" data-test="suggestive" class="mt-8 md:mt-24">
    <p class="mb-3 text-lg font-bold -tracking-[0.15px] md:mb-6">
      {{ t('shoppingCart.suggestive.title') }}
    </p>
    <Swiper slides-per-view="auto">
      <SwiperSlide
        v-for="(sugProd, index) in suggestiveProducts"
        :key="`suggestive-prod-${sugProd.product.identifier}`"
      >
        <CartSuggestiveItem
          data-test="suggestive-item"
          :product="sugProd.product"
          @add-suggestive="addSuggestiveProduct(sugProd.product, index + 1)"
        />
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<style scoped>
.swiper-slide {
  @apply mr-4 w-fit;
}

.swiper {
  @apply p-1;
}
</style>
